import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { hydrateUserFromStorage } from "../../Redux/Slices/authSlice";
import {
  selectIssuesData,
  selectIssuesLoading,
  selectIssuesError,
} from "../../Redux/Slices/issuesSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import Typography from "../../Utility/Typography";
import IssueList from "./IssueList";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForFilter,
  mapSystemListItemToOption,
  ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
} from "./issueMappers";
import { ISSUES_LISTING_FILTER_DEFAULT } from "../Projects/project-detail/issues/issues.mapper";

const MyIssue = ({
  showEpics = false,
  onShowEpicsChange,
  onOpenWorkItemDetail,
  registerGlobalListRefetch,
}) => {
  const dispatch = useDispatch();
  const issuesData = useSelector(selectIssuesData);
  const loading = useSelector(selectIssuesLoading);
  const error = useSelector(selectIssuesError);
  const usersData = useSelector(selectUsersData);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const currentUser = useSelector((state) => state.auth?.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [listingFilter, setListingFilter] = useState({
    ...ISSUES_LISTING_FILTER_DEFAULT,
    project: [],
    dueBefore: "",
    dueAfter: "",
  });
  const [typeOptions, setTypeOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
    dispatch(fetchClientsData({ getAll: true }));
    dispatch(fetchProjectsData({ getAll: true }));
    if (!currentUser) {
      dispatch(hydrateUserFromStorage());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "WORK_ITEM_TYPE", context: "my-issue-type-filter" }))
      .unwrap()
      .then((list) => {
        setTypeOptions(
          Array.isArray(list) ? list.map((item) => mapSystemListItemToOption(item)) : []
        );
      })
      .catch(() => setTypeOptions([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "PRIORITY", context: "my-issue-priority-filter" }))
      .unwrap()
      .then((list) => {
        setPriorityOptions(
          Array.isArray(list) ? list.map((item) => mapSystemListItemToOption(item)) : []
        );
      })
      .catch(() => setPriorityOptions([]));
  }, [dispatch]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterReset = () => {
    setListingFilter({
      ...ISSUES_LISTING_FILTER_DEFAULT,
      project: [],
      dueBefore: "",
      dueAfter: "",
    });
    onShowEpicsChange?.(false);
    setCurrentPage(1);
  };

  const assigneeOptions = getListFromApiResponse(usersData).map(mapUserToAssigneeOptionForFilter);

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {error}
          </Typography>
        </div>
      </div>
    );
  }

  const issuesDataForList = issuesData ?? { list: [] };

  return (
    <IssueList
      issuesData={issuesDataForList}
      loading={loading}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      listingFilter={listingFilter}
      showEpics={showEpics}
      onShowEpicsChange={onShowEpicsChange}
      handleFilterChange={handleFilterChange}
      handleFilterReset={handleFilterReset}
      assigneeOptions={assigneeOptions}
      filterMode="my"
      clientsData={clientsData}
      projectsData={projectsData}
      typeOptions={typeOptions}
      statusOptions={ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS}
      priorityOptions={priorityOptions}
      onOpenWorkItemDetail={onOpenWorkItemDetail}
      registerGlobalListRefetch={registerGlobalListRefetch}
    />
  );
};

export default MyIssue;
